import { useMemo, useState } from "react";
import { Plus, Pencil, Trash2, Search, FileUp } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageHeader, Loading } from "@/components/Common";
import { SimpleSelect, dbOptions, matchesOption } from "@/components/Filters";
import { AsinDialog, ContentsCell } from "@/components/AsinDialog";
import { AsinUploadDialog } from "@/components/AsinUpload";
import { AsinRepairBanner } from "@/components/AsinRepair";
import { useAuth } from "@/context/AuthContext";
import { useLoad } from "@/hooks/useData";
import { api, errMsg } from "@/lib/api";

const matches = (a, s) => [a.asin, a.seller_sku, a.contents_note, ...a.components.map((c) => c.name), ...(a.extra_contents || []).map((x) => x.name)]
  .some((t) => (t || "").toLowerCase().includes(s));

const RowActions = ({ a, onEdit, onDelete }) => (
  <>
    <button className="p-1.5 text-muted-foreground hover:text-primary" onClick={() => onEdit(a)} data-testid={`edit-asin-${a.asin}`}><Pencil className="h-4 w-4" /></button>
    <button className="p-1.5 text-muted-foreground hover:text-red-400" onClick={() => onDelete(a)} data-testid={`delete-asin-${a.asin}`}><Trash2 className="h-4 w-4" /></button>
  </>
);

const AsinCards = ({ rows, isAdmin, onEdit, onDelete }) => (
  <div className="space-y-3 md:hidden" data-testid="asin-catalogue-cards">
    {rows.map((a) => (
      <div key={a.id} className="panel p-3 text-xs" data-testid={`asin-card-${a.asin}`}>
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="font-mono font-bold text-primary">{a.asin}</div>
            <div className="truncate">{a.seller_sku}</div>
            <div className="text-muted-foreground truncate">{[a.group, a.size].filter(Boolean).join(" · ")}</div>
          </div>
          {isAdmin && <div className="flex shrink-0"><RowActions a={a} onEdit={onEdit} onDelete={onDelete} /></div>}
        </div>
        <div className="mt-2 border-t border-border pt-2"><div className="mb-1 text-[10px] uppercase tracking-wider text-muted-foreground">Contents per unit</div><ContentsCell a={a} /></div>
      </div>
    ))}
  </div>
);

const AsinTable = ({ rows, isAdmin, onEdit, onDelete }) => (
  <div className="panel hidden md:block overflow-auto max-h-[calc(100vh-260px)]">
    <table className="data-table" data-testid="asin-catalogue-table">
      <thead><tr><th>ASIN</th><th>Contents per unit</th><th>Seller SKU</th><th>Group</th><th>Size</th><th></th></tr></thead>
      <tbody>
        {rows.map((a) => (
          <tr key={a.id} data-testid={`asin-row-${a.asin}`}>
            <td className="font-mono text-xs font-bold text-primary">{a.asin}</td>
            <td className="text-xs min-w-[260px]"><ContentsCell a={a} /></td>
            <td className="text-xs max-w-[180px] truncate" title={a.seller_sku}>{a.seller_sku}</td>
            <td className="text-xs max-w-[180px] truncate">{a.group}<div className="text-muted-foreground">{a.category}</div></td>
            <td className="text-xs max-w-[140px] truncate" title={a.size}>{a.size}</td>
            <td className="whitespace-nowrap">{isAdmin && <RowActions a={a} onEdit={onEdit} onDelete={onDelete} />}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default function AsinCatalogue() {
  const { isAdmin } = useAuth();
  const { data, reload } = useLoad(() => api.get("/asins").then((r) => r.data));
  const [uploadOpen, setUploadOpen] = useState(false);
  const [q, setQ] = useState("");
  const [group, setGroup] = useState("all");
  const [show, setShow] = useState("all");
  const [edit, setEdit] = useState({ open: false, asin: null });
  const groups = useMemo(() => dbOptions((data || []).map((a) => a.group), "(No group)"), [data]);
  const choices = useMemo(() => {
    const uniq = (k) => [...new Set((data || []).map((a) => (a[k] || "").trim()).filter(Boolean))].sort((x, y) => x.localeCompare(y));
    return { group: uniq("group"), category: uniq("category") };
  }, [data]);
  const rows = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (data || []).filter((a) => matchesOption(a.group, group) && (show === "all" || !a.components.length) && (!s || matches(a, s)));
  }, [data, q, group, show]);
  if (!data) return <Loading />;
  const del = async (a) => {
    if (!window.confirm(`Delete ASIN ${a.asin} from the catalogue? Past ASIN sales stay recorded.`)) return;
    try { await api.delete(`/asins/${a.id}`); toast.success("ASIN deleted"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const onEdit = (a) => setEdit({ open: true, asin: a });
  const missing = data.filter((a) => !a.components.length).length;
  const props = { rows, isAdmin, onEdit, onDelete: del };
  return (
    <div>
      <PageHeader title="ASIN Catalogue" subtitle="Which products (and how many) go out for each Amazon ASIN sold.">
        {isAdmin && <Button size="sm" variant="outline" onClick={() => setUploadOpen(true)} data-testid="upload-asin-sheet-btn"><FileUp className="h-4 w-4" /> Upload sheet</Button>}
        {isAdmin && <Button size="sm" onClick={() => setEdit({ open: true, asin: null })} data-testid="add-asin-btn"><Plus className="h-4 w-4" /> Add ASIN</Button>}
      </PageHeader>
      {isAdmin && <div className="mb-4"><AsinRepairBanner onDone={reload} /></div>}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative w-full sm:w-72">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search ASIN, SKU or contents…" className="pl-8 h-9" data-testid="asin-catalogue-search" />
        </div>
        <SimpleSelect value={group} onChange={setGroup} testid="asin-catalogue-group" className="h-9 w-full sm:w-64" options={[["all", "All product groups"], ...groups]} />
        <SimpleSelect value={show} onChange={setShow} testid="asin-catalogue-show" className="h-9 w-52" options={[["all", "All ASINs"], ["missing", `Needs breakdown (${missing})`]]} />
        <span className="ml-auto font-mono text-xs text-muted-foreground" data-testid="asin-catalogue-count">{rows.length} / {data.length} ASINs</span>
      </div>
      <AsinTable {...props} />
      <AsinCards {...props} />
      <AsinDialog open={edit.open} asin={edit.asin} choices={choices} onSaved={reload} onOpenChange={(o) => setEdit((s) => ({ ...s, open: o }))} />
      <AsinUploadDialog open={uploadOpen} onOpenChange={setUploadOpen} onSaved={reload} />
    </div>
  );
}
